import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { DialogFlexSales } from '../components';
import { PermissionsProvider } from '../components/Permissions';
import { CountrySelect, StoreSelect } from '../components/selects';
import ParametricStoresForm from '../components/ParametricStoresForm/ParametricStoresForm';
import { parametricStoresDeleteRequested, parametricStoresListRequested } from '../state';
import {
  parametricStoresDefaultCountrySelector,
  parametricStoresEnabledCountriesSelector,
  parametricStoresListSelector,
} from '../state/selectors';
import { ParametricStore } from '../state/parametricStores/types';
import { AppPermissions, Stores } from '../types';

const ParametricStores = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const listState = useSelector(parametricStoresListSelector);

  const defaultCountry = useSelector(parametricStoresDefaultCountrySelector);
  const enabledCountries = useSelector(parametricStoresEnabledCountriesSelector);

  const [country, setCountry] = useState('');
  const [storeFilter, setStoreFilter] = useState<Stores[]>([]);
  const [codeFilter, setCodeFilter] = useState('');

  // Arranca en el pais que sale del parametro; si el usuario tiene mas de uno, lo puede cambiar.
  useEffect(() => {
    if (!country && defaultCountry) setCountry(defaultCountry);
  }, [country, defaultCountry]);

  // Al cambiar de pais el filtro de local no se arrastra: los locales son de otro pais.
  useEffect(() => {
    setStoreFilter([]);
  }, [country]);

  // El filtro se resuelve sobre lo que ya se trajo: la tabla no tiene indice por esas columnas.
  const rows = useMemo(() => {
    const porCodigo = codeFilter.trim().toLowerCase();
    return listState.data.filter(
      item =>
        (storeFilter.length === 0 || storeFilter.some(i => i.storeAcronym === item.store)) &&
        (!porCodigo || item.code.toLowerCase().includes(porCodigo))
    );
  }, [listState.data, storeFilter, codeFilter]);

  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<ParametricStore | undefined>(undefined);
  const [deleteItem, setDeleteItem] = useState<ParametricStore | undefined>(undefined);

  useEffect(() => {
    if (country) dispatch(parametricStoresListRequested(country));
  }, [dispatch, country]);

  const handleCreate = useCallback(() => {
    setEditItem(undefined);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((item: ParametricStore) => {
    setEditItem(item);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditItem(undefined);
  }, []);

  const handleDeleteOpen = useCallback((item: ParametricStore) => {
    setDeleteItem(item);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (deleteItem) {
      dispatch(parametricStoresDeleteRequested(deleteItem.country, deleteItem.code));
      setDeleteItem(undefined);
    }
  }, [deleteItem, dispatch]);

  const handleDeleteClose = useCallback(() => {
    setDeleteItem(undefined);
  }, []);

  return (
    <PermissionsProvider resourcePermissions={[AppPermissions.PARAMETRIC_STORES]}>
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">{t('parametricStores.title')}</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            {t('parametricStores.create')}
          </Button>
        </Box>

        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <CountrySelect
            simple
            value={country}
            onChange={setCountry}
            allowedCountryCodes={enabledCountries}
            sx={{ minWidth: '12em' }}
          />
          <Box minWidth="18em">
            <StoreSelect
              label={t('parametricStores.filters.store')}
              countryCode={country}
              value={storeFilter}
              onChange={setStoreFilter}
              selectAll
            />
          </Box>
          <TextField
            size="small"
            label={t('parametricStores.filters.code')}
            value={codeFilter}
            onChange={e => setCodeFilter(e.target.value)}
          />
        </Box>

        {listState.loading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}

        {!listState.loading && (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('parametricStores.columns.country')}</TableCell>
                  <TableCell>{t('parametricStores.columns.code')}</TableCell>
                  <TableCell>{t('parametricStores.columns.store')}</TableCell>
                  <TableCell>{t('parametricStores.columns.store_name')}</TableCell>
                  <TableCell align="center">{t('parametricStores.columns.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(item => (
                  <TableRow key={`${item.country}-${item.code}`}>
                    <TableCell>{item.country}</TableCell>
                    <TableCell>{item.code}</TableCell>
                    <TableCell>{item.store}</TableCell>
                    <TableCell>{item.store_name}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleEdit(item)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteOpen(item)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {listState.loaded && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t('parametricStores.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <DialogFlexSales open={formOpen} onClose={handleFormClose} maxWidth="sm" fullWidth>
          <DialogTitle id="draggable-dialog-title">
            {editItem
              ? t('parametricStores.form.titleEdit')
              : t('parametricStores.form.titleCreate')}
          </DialogTitle>
          <DialogContent>
            <Box pt={1}>
              <ParametricStoresForm
                editItem={editItem}
                onClose={handleFormClose}
                country={country}
              />
            </Box>
          </DialogContent>
        </DialogFlexSales>

        <DialogFlexSales open={Boolean(deleteItem)} onClose={handleDeleteClose} maxWidth="xs">
          <DialogTitle id="draggable-dialog-title">
            {t('parametricStores.delete.title')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('parametricStores.delete.confirm', {
                code: deleteItem?.code,
                store: deleteItem?.store,
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteClose} variant="contained">
              {t('parametricStores.delete.cancel')}
            </Button>
            <Button onClick={handleDeleteConfirm} variant="contained" color="error">
              {t('parametricStores.delete.confirm_button')}
            </Button>
          </DialogActions>
        </DialogFlexSales>
      </Box>
    </PermissionsProvider>
  );
};

export default ParametricStores;
